/**
 * Pharmacy contracts.
 *
 * Quantities on the wire are whole numbers. A selling-unit count is
 * converted to base units on the server from the unit's own definition —
 * a client never states how many tablets a strip holds.
 */

import { z } from 'zod';
import {
  ADJUSTMENT_REASONS,
  REFUND_DISPOSITIONS,
  RX_CLASSES,
  STORAGE_REQUIREMENTS,
} from '../pharmacy.js';
import { PAYMENT_METHODS } from '../enums.js';
import { barcodeSchema } from './shop.js';
import {
  isoDateSchema,
  isoDateTimeSchema,
  longTextSchema,
  nonNegativeMoneySchema,
  positiveMoneySchema,
  shortTextSchema,
  uuidSchema,
} from '../primitives.js';

const wholePositive = z.number().int('Whole units only').positive().max(10_000_000);
const wholeNonNegative = z.number().int('Whole units only').min(0).max(10_000_000);
const optionalText = (max = 200) => z.string().trim().max(max).nullable().optional();
const idempotencyKey = z.string().trim().min(8).max(120).optional();

// ── Settings ───────────────────────────────────────────────────────────────

export const pharmacySettingsSchema = z.object({
  expiryAlertDays: z.number().int().min(0).max(1095).optional(),
  sellCutoffDays: z.number().int().min(0).max(365).optional(),
  maxDiscountPercent: z.string().regex(/^\d{1,3}(\.\d{1,2})?$/).optional(),
  enforcePrescriptions: z.boolean().optional(),
  requireRegisteredReviewer: z.boolean().optional(),
  restrictedRequiresWitness: z.boolean().optional(),
});
export type PharmacySettingsInput = z.infer<typeof pharmacySettingsSchema>;

/**
 * Selling restricted medicines, switched on only with the regulatory sign-off
 * named: who signed, what document, when. The owner's decision; switching it
 * off is anybody's who manages pharmacy settings.
 */
export const restrictedSalesSchema = z.discriminatedUnion('enabled', [
  z.object({ enabled: z.literal(true), signOff: z.string().trim().min(3).max(500) }),
  z.object({ enabled: z.literal(false), reason: z.string().trim().min(3).max(500) }),
]);
export type RestrictedSalesInput = z.infer<typeof restrictedSalesSchema>;

/**
 * Loyalty points (0159): earning and redeeming, each its own switch, and the
 * two rates. Switching either on, or changing a rate, is the owner's decision;
 * switching off is anybody's who manages pharmacy settings. A reason is kept
 * with every change.
 */
export const loyaltySettingsSchema = z.object({
  earningEnabled: z.boolean(),
  redemptionEnabled: z.boolean(),
  /** Non-medicine value paid for one point, e.g. "100.00". */
  spendPerPoint: z.string().regex(/^\d{1,9}(\.\d{1,2})?$/, 'An amount, e.g. 100.00'),
  /** What one point is worth when redeemed, e.g. "1.00". */
  pointValue: z.string().regex(/^\d{1,9}(\.\d{1,2})?$/, 'An amount, e.g. 1.00'),
  reason: z.string().trim().min(3).max(500),
});
export type LoyaltySettingsInput = z.infer<typeof loyaltySettingsSchema>;

export const pharmacistSchema = z.object({
  userId: uuidSchema,
  registrationNumber: z.string().trim().min(2).max(60),
  registrationExpiresOn: isoDateSchema.nullable().optional(),
});

export const jobPresetSchema = z.object({
  preset: z.enum(['pharmacist', 'cashier', 'stockkeeper']),
});

// ── Catalogue ──────────────────────────────────────────────────────────────

export const pharmacyUnitInputSchema = z.object({
  id: uuidSchema.optional(),
  name: z.string().trim().min(1).max(40),
  baseQuantity: wholePositive,
  barcode: z.string().trim().min(3).max(64).nullable().optional(),
  price: nonNegativeMoneySchema.nullable().optional(),
  sellable: z.boolean().default(true),
  purchasable: z.boolean().default(true),
});
export type PharmacyUnitInput = z.infer<typeof pharmacyUnitInputSchema>;

export const medicineInputSchema = z.object({
  name: shortTextSchema,
  sku: z.string().trim().max(60).nullable().optional(),
  categoryId: uuidSchema.nullable().optional(),
  isMedicine: z.boolean().default(true),
  genericName: optionalText(),
  brandName: optionalText(),
  strength: optionalText(80),
  dosageForm: optionalText(80),
  manufacturer: optionalText(),
  registrationNumber: optionalText(80),
  /** A medicine's class and storage are the pharmacist's to state; neither is ever assumed. */
  rxClass: z.enum(RX_CLASSES).optional(),
  storageRequirement: z.enum(STORAGE_REQUIREMENTS).optional(),
  tracksBatches: z.boolean().default(true),
  baseUnitName: z.string().trim().min(1).max(40),
  baseUnitBarcode: z.string().trim().min(3).max(64).nullable().optional(),
  baseUnitPrice: nonNegativeMoneySchema.nullable().optional(),
  baseUnitSellable: z.boolean().default(true),
  /** Larger units: strip, pack, carton. Each a whole number of base units. */
  units: z.array(pharmacyUnitInputSchema).max(10).default([]),
});
export type MedicineInput = z.infer<typeof medicineInputSchema>;

export const medicineUpdateSchema = medicineInputSchema
  .omit({ units: true, baseUnitName: true, baseUnitBarcode: true, baseUnitPrice: true, baseUnitSellable: true })
  .partial()
  .extend({
    /**
     * Why the class or medicine status is changing. Required whenever either
     * does, and kept in the audit trail with the before and after.
     */
    classificationReason: z.string().trim().min(5).max(500).optional(),
  });

export const productLocationSchema = z.object({
  branchId: uuidSchema,
  shelfId: uuidSchema.nullable().optional(),
  reorderLevel: wholeNonNegative.nullable().optional(),
});

export const shelfInputSchema = z.object({
  branchId: uuidSchema,
  code: z.string().trim().min(1).max(40),
  name: optionalText(80),
  storageAreaId: uuidSchema.nullable().optional(),
});

// ── Stock ──────────────────────────────────────────────────────────────────

export const holdInputSchema = z.object({
  batchId: uuidSchema,
  branchId: uuidSchema.nullable().optional(),
  reason: z.string().trim().min(3).max(2000),
});

export const releaseHoldSchema = z.object({
  decision: z.enum(['release', 'dispose', 'return_to_supplier']),
  note: z.string().trim().min(3).max(2000),
});

/** A pharmacist releasing many batches awaiting their check at once, with one note for all of them. */
export const releaseChecksSchema = z.object({
  holdIds: z.array(z.string().uuid()).min(1).max(500),
  note: z.string().trim().min(5).max(2000),
});
export type ReleaseChecksInput = z.infer<typeof releaseChecksSchema>;

/**
 * Correcting what a batch record says — its number, expiry, manufacturing
 * date or cost per base unit — when it was entered wrongly. A quantity is
 * corrected by an adjustment, never here. Every correction states why.
 */
export const batchCorrectionSchema = z
  .object({
    batchNumber: z.string().trim().min(1).max(80).optional(),
    expiryDate: isoDateSchema.optional(),
    manufacturedOn: isoDateSchema.nullable().optional(),
    /** Per base unit. */
    unitCost: nonNegativeMoneySchema.optional(),
    reason: z.string().trim().min(5).max(1000),
  })
  .refine(
    (v) => v.batchNumber !== undefined || v.expiryDate !== undefined || v.manufacturedOn !== undefined || v.unitCost !== undefined,
    { message: 'Say what to correct', path: ['batchNumber'] },
  );
export type BatchCorrectionInput = z.infer<typeof batchCorrectionSchema>;

/**
 * An owner's correction of one opening-stock entry: what it should have been.
 * The quantity may be zero (nothing was really there); the cost is what one
 * base unit really cost, never zero by assumption.
 */
export const openingCorrectionSchema = z
  .object({
    quantity: z.number().int().min(0),
    /** Required when stock remains; with a quantity of zero the entry's cost is carried over. */
    unitCost: positiveMoneySchema.optional(),
    reason: z.string().trim().min(5).max(1000),
    /** What the entry stood at when the owner looked; a different figure now refuses the correction. */
    expectedQuantity: z.number().int().min(0).optional(),
    idempotencyKey,
  })
  .refine((v) => v.quantity === 0 || v.unitCost !== undefined, { message: 'Enter what one unit cost', path: ['unitCost'] });
export type OpeningCorrectionInput = z.infer<typeof openingCorrectionSchema>;

export const adjustmentInputSchema = z.object({
  branchId: uuidSchema,
  productId: uuidSchema,
  batchId: uuidSchema.nullable().optional(),
  /** Signed, in base units. Negative removes stock. */
  quantity: z.number().int().refine((v) => v !== 0, 'A zero adjustment changes nothing'),
  reason: z.enum(ADJUSTMENT_REASONS),
  note: optionalText(1000),
  /** What the person saw on hand before confirming; a different figure now refuses the change. */
  expectedOnHand: z.number().int().min(0).optional(),
  idempotencyKey,
});
export type AdjustmentInput = z.infer<typeof adjustmentInputSchema>;

export const decisionSchema = z.object({
  approve: z.boolean(),
  note: optionalText(1000),
});

export const disposalInputSchema = z.object({
  method: z.string().trim().min(2).max(200),
  witnessedByName: optionalText(200),
  disposedOn: isoDateSchema,
  note: optionalText(1000),
});

export const countStartSchema = z.object({
  branchId: uuidSchema,
  productIds: z.array(uuidSchema).max(2000).optional(),
  note: optionalText(500),
});

export const countLineSchema = z.object({
  lineId: uuidSchema,
  countedQuantity: wholeNonNegative,
});

// ── Purchasing ─────────────────────────────────────────────────────────────

export const purchaseOrderInputSchema = z.object({
  supplierId: uuidSchema,
  branchId: uuidSchema,
  expectedOn: isoDateSchema.nullable().optional(),
  note: optionalText(1000),
  lines: z
    .array(
      z.object({
        productId: uuidSchema,
        unitId: uuidSchema,
        quantity: wholePositive,
        unitCost: nonNegativeMoneySchema,
      }),
    )
    .min(1)
    .max(500),
  idempotencyKey,
});
export type PurchaseOrderInput = z.infer<typeof purchaseOrderInputSchema>;

export const receiptInputSchema = z.object({
  supplierId: uuidSchema,
  branchId: uuidSchema,
  purchaseOrderId: uuidSchema.nullable().optional(),
  supplierInvoiceId: uuidSchema.nullable().optional(),
  receivedOn: isoDateSchema,
  note: optionalText(1000),
  lines: z
    .array(
      z.object({
        poLineId: uuidSchema.nullable().optional(),
        productId: uuidSchema,
        unitId: uuidSchema,
        quantity: wholePositive,
        unitCost: nonNegativeMoneySchema,
        batchNumber: z.string().trim().min(1).max(80).nullable().optional(),
        expiryDate: isoDateSchema.nullable().optional(),
        /** As printed on the pack. Optional; never derived. */
        manufacturedOn: isoDateSchema.nullable().optional(),
        /** For a cool, refrigerated or frozen item: it is kept as its storage requirement says. */
        storageConfirmed: z.boolean().optional(),
      }),
    )
    .min(1)
    .max(500),
  /** Post immediately: write batches and stock in the same request. */
  post: z.boolean().default(true),
  idempotencyKey,
});
export type ReceiptInput = z.infer<typeof receiptInputSchema>;

/**
 * Opening stock typed into CenoX: what is already on the shelf at one
 * location, one product or many. Validated and approved exactly as an
 * opening-stock sheet is; nothing here is written until a stock approver
 * approves it. Quantities are in base units (tablets, bottles). Nothing is
 * filled in: a batch-tracked line without its batch, expiry or cost is held
 * as incomplete, never completed for the person.
 */
export const stockEntryInputSchema = z.object({
  branchId: uuidSchema,
  lines: z
    .array(
      z.object({
        productId: uuidSchema,
        quantity: wholePositive,
        batchNumber: z.string().trim().min(1).max(80).nullable().optional(),
        expiryDate: isoDateSchema.nullable().optional(),
        manufacturedOn: isoDateSchema.nullable().optional(),
        unitCost: nonNegativeMoneySchema.nullable().optional(),
        supplierId: uuidSchema.nullable().optional(),
        /** For a cool, refrigerated or frozen item: it is kept as its storage requirement says. */
        storageConfirmed: z.boolean().optional(),
      }),
    )
    .min(1)
    .max(500),
});
export type StockEntryInput = z.infer<typeof stockEntryInputSchema>;

export const supplierInvoiceInputSchema = z.object({
  supplierId: uuidSchema,
  branchId: uuidSchema,
  invoiceNumber: z.string().trim().min(1).max(80),
  invoiceDate: isoDateSchema,
  dueDate: isoDateSchema.nullable().optional(),
  amount: positiveMoneySchema,
  purchaseOrderId: uuidSchema.nullable().optional(),
  note: optionalText(1000),
});

export const supplierPaymentInputSchema = z.object({
  supplierId: uuidSchema,
  branchId: uuidSchema,
  supplierInvoiceId: uuidSchema.nullable().optional(),
  amount: positiveMoneySchema,
  method: z.enum(PAYMENT_METHODS),
  reference: optionalText(120),
  paidOn: isoDateSchema,
  note: optionalText(1000),
  idempotencyKey,
});

export const supplierReturnInputSchema = z.object({
  supplierId: uuidSchema,
  branchId: uuidSchema,
  batchId: uuidSchema,
  baseQuantity: wholePositive,
  creditAmount: nonNegativeMoneySchema,
  reason: z.string().trim().min(3).max(1000),
  idempotencyKey,
});

// ── Transfers ──────────────────────────────────────────────────────────────

export const transferInputSchema = z.object({
  fromBranchId: uuidSchema,
  toBranchId: uuidSchema,
  note: optionalText(1000),
  lines: z.array(z.object({ batchId: uuidSchema, quantity: wholePositive })).min(1).max(500),
  idempotencyKey,
});

export const transferReceiveSchema = z.object({
  lines: z
    .array(
      z.object({
        lineId: uuidSchema,
        receivedQuantity: wholeNonNegative,
        discrepancyReason: optionalText(500),
      }),
    )
    .min(1),
});

export const transferReconcileSchema = z.object({
  lines: z
    .array(z.object({ lineId: uuidSchema, resolution: z.enum(['written_off', 'returned_to_source']) }))
    .min(1),
  note: z.string().trim().min(3).max(1000),
});

// ── Till ───────────────────────────────────────────────────────────────────

export const pharmacyCheckoutLineSchema = z
  .object({
    productId: uuidSchema.optional(),
    unitId: uuidSchema.optional(),
    barcode: z.string().trim().min(3).max(64).optional(),
    quantity: wholePositive,
    /** Per line, in money. Needs `pharmacy:discount`. */
    discount: nonNegativeMoneySchema.optional(),
    prescriptionItemId: uuidSchema.optional(),
  })
  .refine((l) => Boolean(l.unitId || l.barcode), 'Name a unit or scan a barcode');
export type PharmacyCheckoutLine = z.infer<typeof pharmacyCheckoutLineSchema>;

export const tenderSchema = z.object({
  method: z.enum(PAYMENT_METHODS),
  amount: positiveMoneySchema,
  reference: optionalText(120),
  /**
   * For card, transfer and terminal payments: the cashier has SEEN the
   * approval (terminal slip, bank alert). Anything else is recorded as
   * pending and does not count as paid.
   */
  confirmed: z.boolean().default(false),
});

export const pharmacyCheckoutSchema = z.object({
  branchId: uuidSchema,
  lines: z.array(pharmacyCheckoutLineSchema).min(1).max(200),
  tenders: z.array(tenderSchema).max(6).default([]),
  /** Who bought, on any sale — cash, card, transfer or credit. Omitted, it is a walk-in. */
  customerId: uuidSchema.nullable().optional(),
  /**
   * A customer registered at the counter, in place of `customerId`. Found by
   * phone when that phone is already on file, created otherwise — the shop
   * till's rule, so one person is never on file twice.
   */
  newCustomer: z
    .object({
      name: shortTextSchema,
      phone: z.string().trim().max(32).optional(),
    })
    .optional(),
  /** Leave a balance owing. Needs a named customer. */
  allowCredit: z.boolean().default(false),
  basketDiscount: nonNegativeMoneySchema.optional(),
  discountReason: optionalText(300),
  prescriptionId: uuidSchema.nullable().optional(),
  /** Restricted dispensing witness, where settings require one. */
  witnessUserId: uuidSchema.nullable().optional(),
  idempotencyKey: z.string().trim().min(8).max(120),
  notes: optionalText(500),
  /**
   * Loyalty points the named customer spends on this sale, as a discount on
   * its non-medicine lines. Only when the pharmacy has redemption switched on.
   */
  redeemPoints: z.number().int('Whole points only').positive().max(10_000_000).optional(),
});
export type PharmacyCheckoutInput = z.infer<typeof pharmacyCheckoutSchema>;

export const confirmPaymentSchema = z.object({
  outcome: z.enum(['confirmed', 'failed', 'cancelled']),
  providerReference: optionalText(120),
  note: optionalText(500),
});

export const pharmacySalePaymentSchema = z.object({
  amount: positiveMoneySchema,
  method: z.enum(PAYMENT_METHODS),
  reference: optionalText(120),
  confirmed: z.boolean().default(false),
  idempotencyKey,
});

export const heldCartSchema = z.object({
  branchId: uuidSchema,
  label: z.string().trim().min(1).max(80),
  lines: z.array(pharmacyCheckoutLineSchema).min(1).max(200),
});

export const tillOpenSchema = z.object({ branchId: uuidSchema, openingFloat: nonNegativeMoneySchema });
export const tillCloseSchema = z.object({ countedCash: nonNegativeMoneySchema, note: optionalText(500) });

export const refundInputSchema = z.object({
  saleId: uuidSchema,
  method: z.enum(PAYMENT_METHODS).refine((m) => m !== 'credit', 'A refund pays money back'),
  reason: z.string().trim().min(3).max(1000),
  lines: z
    .array(
      z.object({
        saleItemId: uuidSchema,
        /** In the units it was sold in. */
        unitQuantity: wholePositive,
        goodsReturned: z.boolean(),
      }),
    )
    .min(1),
  idempotencyKey: z.string().trim().min(8).max(120),
});

export const dispositionSchema = z.object({
  disposition: z.enum(REFUND_DISPOSITIONS),
  note: z.string().trim().min(3).max(1000),
});

// ── Patients and prescriptions ─────────────────────────────────────────────

export const patientInputSchema = z.object({
  fullName: shortTextSchema,
  dateOfBirth: isoDateSchema.nullable().optional(),
  sex: z.enum(['female', 'male', 'other', 'unknown']).nullable().optional(),
  phone: optionalText(40),
  email: z.string().trim().email().max(200).nullable().optional(),
  address: optionalText(500),
  allergies: z.array(z.string().trim().min(1).max(120)).max(50).default([]),
  clinicalNotes: optionalText(4000),
  reminderConsent: z.boolean().default(false),
  customerId: uuidSchema.nullable().optional(),
});
export type PatientInput = z.infer<typeof patientInputSchema>;

export const prescriberInputSchema = z.object({
  fullName: shortTextSchema,
  registrationNumber: optionalText(60),
  facility: optionalText(200),
  phone: optionalText(40),
});

export const prescriptionInputSchema = z.object({
  branchId: uuidSchema,
  patientId: uuidSchema,
  prescriberId: uuidSchema.nullable().optional(),
  issuedOn: isoDateSchema.nullable().optional(),
  source: z.enum(['paper', 'electronic', 'verbal', 'repeat']).default('paper'),
  fulfilment: z.enum(['pickup', 'delivery']).default('pickup'),
  note: optionalText(1000),
  items: z
    .array(
      z.object({
        productId: uuidSchema,
        prescribedText: z.string().trim().min(1).max(500),
        prescribedQuantity: wholePositive.nullable().optional(),
      }),
    )
    .min(1)
    .max(50),
});

export const prescriptionReviewSchema = z.object({
  decision: z.enum(['approve', 'reject']),
  note: optionalText(2000),
  items: z
    .array(
      z.object({
        itemId: uuidSchema,
        /** Base units the pharmacist approves for dispensing. */
        approvedQuantity: wholeNonNegative,
        /** The directions to print, in the pharmacist's own words. */
        approvedDirections: z.string().trim().min(3).max(500),
      }),
    )
    .default([]),
});

export const deliveryStatusSchema = z.object({
  deliveryStatus: z.enum(['awaiting', 'ready', 'out_for_delivery', 'delivered', 'failed']),
});

// ── Storage and recalls ────────────────────────────────────────────────────

export const storageAreaInputSchema = z.object({
  branchId: uuidSchema,
  name: z.string().trim().min(1).max(80),
  kind: z.enum(['room', 'cool', 'refrigerator', 'freezer']).default('room'),
  minCelsius: z.number().min(-80).max(80),
  maxCelsius: z.number().min(-80).max(80),
});

export const temperatureLogSchema = z.object({
  storageAreaId: uuidSchema,
  readingCelsius: z.number().min(-80).max(80),
  recordedAt: isoDateTimeSchema.optional(),
  note: optionalText(500),
});

export const excursionDecisionSchema = z.object({
  decision: z.enum(['no_impact', 'quarantine', 'dispose']),
  note: z.string().trim().min(3).max(2000),
  /** Batches to quarantine when the decision is `quarantine`. */
  batchIds: z.array(uuidSchema).max(500).default([]),
});

export const recallInputSchema = z.object({
  productId: uuidSchema,
  batchNumbers: z.array(z.string().trim().min(1).max(80)).min(1).max(100),
  reason: z.string().trim().min(3).max(2000),
  authorityReference: optionalText(200),
  /** Place a recall hold on every matching batch at every location. */
  quarantine: z.boolean().default(true),
});

export const recallActionSchema = z.object({
  action: z.enum([
    'stock_quarantined',
    'returned_to_supplier',
    'destroyed',
    'customer_contacted',
    'prescriber_contacted',
    'regulator_notified',
    'note',
  ]),
  branchId: uuidSchema.nullable().optional(),
  batchId: uuidSchema.nullable().optional(),
  quantity: wholeNonNegative.nullable().optional(),
  note: optionalText(2000),
});

// ── Reports ────────────────────────────────────────────────────────────────

export const pharmacyReportQuerySchema = z.object({
  from: isoDateSchema,
  to: isoDateSchema,
  branchId: uuidSchema.optional(),
});

// ── Outage back-entry ──────────────────────────────────────────────────────

export const outageEntrySchema = z.object({
  branchId: uuidSchema,
  /** The reference written on the paper outage log. Entered once per location. */
  outageLogRef: z.string().trim().min(1).max(80),
  /** When the sale really happened, from the paper log. */
  originalOccurredAt: isoDateTimeSchema,
  /** Who made the sale at the counter. A member where known; a name always. */
  originalStaffUserId: uuidSchema.nullable().optional(),
  originalStaffName: z.string().trim().min(1).max(200),
  lines: z
    .array(
      z.object({
        unitId: uuidSchema,
        quantity: wholePositive,
        /** The batch written on the log. Omitted: earliest-expiry as of the original time. */
        batchId: uuidSchema.nullable().optional(),
        prescriptionItemId: uuidSchema.optional(),
      }),
    )
    .min(1)
    .max(200),
  tenders: z
    .array(
      z.object({
        method: z.enum(PAYMENT_METHODS),
        amount: positiveMoneySchema,
        /** Slip number, bank alert or transfer reference. Required for anything but cash. */
        reference: optionalText(120),
        /** The slip or alert has been seen and matched. Otherwise it waits as pending. */
        confirmed: z.boolean().default(false),
      }),
    )
    .max(6)
    .default([]),
  customerId: uuidSchema.nullable().optional(),
  allowCredit: z.boolean().default(false),
  prescriptionId: uuidSchema.nullable().optional(),
  /** Second person who witnessed restricted dispensing, where settings require one. */
  witnessUserId: uuidSchema.nullable().optional(),
  note: optionalText(500),
  idempotencyKey: z.string().trim().min(8).max(120),
});
export type OutageEntryInput = z.infer<typeof outageEntrySchema>;

// ── Medicines & products (every product, classified or not) ────────────────

export const CATALOGUE_STATUSES = ['medicine', 'non_medicine', 'unclassified'] as const;
export type CatalogueStatusKey = (typeof CATALOGUE_STATUSES)[number];

export const pharmacyCatalogueFiltersSchema = z.object({
  search: z.string().trim().max(120).optional(),
  status: z.enum(CATALOGUE_STATUSES).optional(),
  categoryId: uuidSchema.optional(),
  branchId: uuidSchema.optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
export type PharmacyCatalogueFilters = z.infer<typeof pharmacyCatalogueFiltersSchema>;

/**
 * Correcting a product's own details from Medicines & products: its name,
 * codes, prices and category. Classified or not. What it IS (medicine or not,
 * its class, its storage) is classified, never edited here; how much of it
 * there is, and in which batches, is entered through Add stock. A field left
 * out is left as it is; a barcode sent as null is removed.
 */
export const catalogueProductEditSchema = z
  .object({
    name: z.string().trim().min(1).max(200).optional(),
    sku: z.string().trim().max(60).nullable().optional(),
    barcode: barcodeSchema.nullable().optional(),
    costPrice: nonNegativeMoneySchema.nullable().optional(),
    sellingPrice: nonNegativeMoneySchema.nullable().optional(),
    categoryId: uuidSchema.nullable().optional(),
  })
  .strict()
  .refine((v) => Object.values(v).some((x) => x !== undefined), { message: 'Say what to change', path: ['name'] });
export type CatalogueProductEditInput = z.infer<typeof catalogueProductEditSchema>;

/**
 * A pharmacist's decision on existing products: medicine (with the class they
 * chose — there is no default) or verified non-medicine. One reason covers the
 * batch and is kept with every item.
 */
/**
 * A registered pharmacist's review of medicines already classified: each item
 * gets the class they choose, or is found not to be a medicine. One reason for
 * the batch, kept in every item's classification history.
 */
export const catalogueReclassifySchema = z.object({
  items: z
    .array(z.object({ productId: uuidSchema, decision: z.enum([...RX_CLASSES, 'non_medicine']) }))
    .min(1)
    .max(500),
  reason: z.string().trim().min(5).max(500),
});
export type CatalogueReclassifyInput = z.infer<typeof catalogueReclassifySchema>;

export const catalogueClassifySchema = z.object({
  items: z
    .array(
      z.object({
        productId: uuidSchema,
        decision: z.enum(['medicine', 'non_medicine']),
        rxClass: z.enum(RX_CLASSES).optional(),
        storageRequirement: z.enum(STORAGE_REQUIREMENTS).optional(),
        baseUnitName: z.string().trim().min(1).max(40).optional(),
      }),
    )
    .min(1)
    .max(500),
  reason: z.string().trim().min(5).max(500),
});
export type CatalogueClassifyInput = z.infer<typeof catalogueClassifySchema>;
